// @ts-nocheck
import React, { useEffect, useState } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet, Platform, TextInput,
} from 'react-native';
import { Colors, FontSize, FontWeight, Radii } from '../../theme/tokens';
import { useApp } from '../../store/AppStore';
import { useAuth } from '../../store/AuthContext';
import { useResponsive } from '../../theme/responsive';
import { ScreenShell } from '../../components/WebShell';
import { ComingSoon } from '../../components/ComingSoon';
import { Icon } from '../../components/Icon';
import PrimaryButton from '../../components/Button_Component/Primary_Button';
import { Chip } from '../../components/Chip';

// ─── Toggle ───────────────────────────────────────────────────────────────────

function Toggle({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <TouchableOpacity
      onPress={onToggle}
      style={[styles.toggle, { backgroundColor: on ? Colors.accent : Colors.paper3, borderColor: on ? Colors.accent : Colors.lineStrong }]}
    >
      <View style={[styles.toggleThumb, { transform: [{ translateX: on ? 14 : 1 }] }]} />
    </TouchableOpacity>
  );
}

// ─── VerifyBanner ─────────────────────────────────────────────────────────────

function VerifyBanner({
  state, email, onSend, onConfirm,
}: {
  state: string; email: string;
  onSend: () => void; onConfirm: () => void;
}) {
  if (state === 'verified') {
    return (
      <View style={[styles.alert, styles.alertSuccess]}>
        <Icon name="CheckCircle" size={16} color={Colors.success} />
        <View style={styles.alertBody}>
          <Text style={[styles.alertTitle, { color: Colors.success }]}>{email} verified</Text>
          <Text style={styles.alertDesc}>Customer emails are being sent from your verified domain.</Text>
        </View>
      </View>
    );
  }
  if (state === 'pending') {
    return (
      <View style={[styles.alert, styles.alertInfo]}>
        <Icon name="Mail" size={16} color={Colors.info} />
        <View style={styles.alertBody}>
          <Text style={[styles.alertTitle, { color: Colors.info }]}>Verification email sent to {email}</Text>
          <View style={styles.alertDescRow}>
            <Text style={styles.alertDesc}>Click the link in that email to confirm. </Text>
            <TouchableOpacity onPress={onConfirm}>
              <Text style={[styles.alertDesc, styles.alertLink]}>Simulate confirm</Text>
            </TouchableOpacity>
            <Text style={styles.alertDesc}> or </Text>
            <TouchableOpacity onPress={onSend}>
              <Text style={[styles.alertDesc, styles.alertLink]}>resend</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  }
  return (
    <View style={[styles.alert, styles.alertWarn]}>
      <Icon name="Alert" size={16} color={Colors.warn} />
      <View style={styles.alertBody}>
        <Text style={[styles.alertTitle, { color: Colors.warn }]}>{email} is not yet verified</Text>
        <Text style={styles.alertDesc}>Emails will send from noreply@mail.softknack.io until verified.</Text>
      </View>
      <PrimaryButton title="Send verification email" size="sm" variant="primary" onPress={onSend} />
    </View>
  );
}

// ─── SectionH ─────────────────────────────────────────────────────────────────

function SectionH({ title, sub }: { title: string; sub?: string }) {
  return (
    <View style={styles.sectionH}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {sub ? <Text style={styles.sectionSub}>{sub}</Text> : null}
    </View>
  );
}

// ─── Card ─────────────────────────────────────────────────────────────────────

function Card({ children }: { children: React.ReactNode }) {
  return <View style={styles.card}>{children}</View>;
}

function CardRow({ children, last, style }: { children: React.ReactNode; last?: boolean; style?: any }) {
  return (
    <View style={[styles.cardRow, !last && styles.cardRowBorder, style]}>
      {children}
    </View>
  );
}

// ─── EmailChannelScreen ───────────────────────────────────────────────────────

export function EmailChannelScreen() {
  const { channel, updateChannel, sendVerification, confirmVerification, checkDns, saveChannel, loadIntegrationsStatus, toast } = useApp();
  const { org } = useAuth();
  const { isMobile, pagePadX } = useResponsive();

  // Honest integrations status from the server (Option C). Defaults to
  // "not configured" so we never render fabricated DNS records.
  const [integrationsStatus, setIntegrationsStatus] = useState<any>(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      const s = await loadIntegrationsStatus?.();
      if (alive && s) setIntegrationsStatus(s);
    })();
    return () => { alive = false; };
  }, []);
  const dnsConfigured = !!integrationsStatus?.emailDns?.configured;
  const dnsProvider = integrationsStatus?.emailDns?.provider;

  const v = channel?.verification ?? 'unverified';
  const verified = v === 'verified';
  const enabled = channel?.enabled ?? true;
  const senderName = channel?.senderName || org?.name || 'Support';
  const senderEmail = channel?.senderEmail ?? '';
  const inboundAddress = channel?.inboundAddress ?? '';
  const csatDelayHours = channel?.csatDelayHours ?? '24';
  const tagRouting = channel?.tagRouting ?? false;

  // ── Showing the premium "coming soon" card for now. The original Email
  //    Channel screen is preserved below (unreachable) — remove this return to
  //    restore it. Nothing was deleted. ──
  return (
    <ComingSoon
      crumbs={[{ label: 'Settings' }, { label: 'Email Channel' }]}
      icon="mail"
      title="Email Channel"
      subtitle="Connect your support inbox and send beautifully on-brand replies. We're polishing this to feel effortless — it'll be ready before you know it."
    />
  );

  return (
    <ScreenShell crumbs={[
      { label: 'Settings' },
      { label: 'Email Channel' },
    ]}>
      <View style={styles.root}>
        {/* Page Head */}
        <View style={[styles.pageHead, { paddingHorizontal: pagePadX, flexWrap: 'wrap' }]}>
          <View style={{ flex: 1 }}>
            <Text style={styles.pageTitle}>Email Channel</Text>
            <Text style={styles.pageSub}>Configure how customers reach your team by email, and what domain replies are sent from.</Text>
          </View>
          <View style={styles.enabledBadgeRow}>
            <View style={[
              styles.enabledBadge,
              enabled ? styles.enabledBadgeOn : styles.enabledBadgeOff,
            ]}>
              <View style={[styles.enabledDot, { backgroundColor: enabled ? Colors.success : Colors.ink4 }]} />
              <Text style={[styles.enabledText, { color: enabled ? Colors.success : Colors.ink3 }]}>
                {enabled ? 'Enabled' : 'Disabled'}
              </Text>
            </View>
            <Toggle on={enabled} onToggle={() => updateChannel?.({ enabled: !enabled })} />
          </View>
        </View>

        <ScrollView style={styles.body} contentContainerStyle={[styles.bodyContent, { padding: isMobile ? 16 : 24 }]}>
          {/* Sender config */}
          <SectionH title="Sender configuration" sub="Customers see this name and email address on every outbound message." />
          <Card>
            <CardRow>
              <View style={[styles.grid2, isMobile && styles.gridStack]}>
                <View style={styles.field}>
                  <Text style={styles.fieldLabel}>From name</Text>
                  <TextInput
                    style={styles.input}
                    value={senderName}
                    onChangeText={(v) => updateChannel?.({ senderName: v })}
                    placeholder="Acme Support"
                  />
                  <Text style={styles.fieldHint}>Shown as the sender in the customer's inbox.</Text>
                </View>
                <View style={styles.field}>
                  <Text style={styles.fieldLabel}>From email</Text>
                  <View style={styles.inputWithIcon}>
                    <TextInput
                      style={[styles.input, { flex: 1 }]}
                      value={senderEmail}
                      onChangeText={(v) => updateChannel?.({ senderEmail: v, verification: 'unverified', dnsVerified: false })}
                      placeholder="support@acme.com"
                      keyboardType="email-address"
                    />
                    {verified && (
                      <View style={styles.inputSuffix}>
                        <Icon name="CheckCircle" size={14} color={Colors.success} />
                      </View>
                    )}
                  </View>
                  <Text style={styles.fieldHint}>{verified ? 'Verified · ready to send' : 'Verify to send from this address'}</Text>
                </View>
              </View>
              <View style={{ marginTop: 14 }}>
                <VerifyBanner
                  state={v}
                  email={senderEmail}
                  onSend={() => sendVerification?.()}
                  onConfirm={() => confirmVerification?.()}
                />
              </View>
            </CardRow>
          </Card>

          {/* DNS / Domain verification */}
          <SectionH title="Domain verification" sub="How outbound mail proves it comes from your domain so it reaches the inbox, not spam." />
          <Card>
            {dnsConfigured ? (
              <CardRow last>
                <View style={styles.dnsHeadRow}>
                  <View style={styles.dnsHeadLeft}>
                    <Icon name="Server" size={16} color={Colors.ink3} />
                    <View style={{ flex: 1 }}>
                      <Text style={styles.dnsHeadTitle}>
                        DNS verification active{dnsProvider ? ` via ${dnsProvider}` : ''}
                      </Text>
                      <Text style={styles.dnsHeadSub}>
                        SPF and DKIM records for your domain are managed by{dnsProvider ? ` ${dnsProvider}` : ' your email provider'}.
                        Use "Check DNS status" to re-verify after any registrar changes.
                      </Text>
                    </View>
                  </View>
                  <View style={styles.dnsHeadBtns}>
                    <PrimaryButton
                      title="Check DNS status"
                      size="sm"
                      variant="primary"
                      leftIcon={<Icon name="Refresh" size={12} color={Colors.paper} />}
                      onPress={() => checkDns?.()}
                    />
                  </View>
                </View>
              </CardRow>
            ) : (
              <>
                <CardRow>
                  <View style={styles.dnsHeadRow}>
                    <View style={styles.dnsHeadLeft}>
                      <Icon name="Server" size={16} color={Colors.ink3} />
                      <View style={{ flex: 1 }}>
                        <Text style={styles.dnsHeadTitle}>DNS-based domain verification is not configured.</Text>
                        <Text style={styles.dnsHeadSub}>
                          No SPF/DKIM DNS provider is set up for this workspace. Sender verification is
                          done through the email confirmation link instead — verify your sender address below.
                        </Text>
                      </View>
                    </View>
                  </View>
                </CardRow>
                <CardRow last>
                  <View style={styles.field}>
                    <Text style={styles.fieldLabel}>Sender verification</Text>
                    <Text style={styles.fieldHint}>
                      We email a confirmation link to your sender address. Click it to confirm ownership —
                      no DNS records required.
                    </Text>
                    <View style={{ marginTop: 12 }}>
                      <VerifyBanner
                        state={v}
                        email={senderEmail}
                        onSend={() => sendVerification?.()}
                        onConfirm={() => confirmVerification?.()}
                      />
                    </View>
                  </View>
                </CardRow>
              </>
            )}
          </Card>

          {/* Inbound */}
          <SectionH title="Inbound address" sub="Customers who email this address create tickets automatically. Forward your support inbox here, or share it as-is." />
          <Card>
            <CardRow last>
              <View style={styles.inboundRow}>
                <Icon name="MailOpen" size={16} color={Colors.ink3} />
                <Text style={[styles.monoText, styles.inboundAddr]} numberOfLines={1}>{inboundAddress}</Text>
                <PrimaryButton
                  title="Copy"
                  size="sm"
                  variant="default"
                  leftIcon={<Icon name="Copy" size={12} color={Colors.ink} />}
                  onPress={() => toast(`Copied: ${inboundAddress}`, 'info', 'copy')}
                />
              </View>
              <Text style={styles.inboundTip}>
                Tip: set up your DNS MX record to inbound.softknack.io to use support@acme.com directly.
              </Text>
            </CardRow>
          </Card>

          {/* Behaviour */}
          <SectionH title="Behaviour" sub="Fine-tune how the email channel behaves around resolution and routing." />
          <Card>
            <CardRow>
              <View style={styles.behavRow}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.behavTitle}>CSAT email delay</Text>
                  <Text style={styles.behavSub}>Hours to wait after resolution before sending the satisfaction survey. 0 disables.</Text>
                </View>
                <View style={styles.csatDelayRow}>
                  <TextInput
                    style={[styles.input, styles.csatDelayInput]}
                    value={String(csatDelayHours)}
                    onChangeText={(v) => updateChannel?.({ csatDelayHours: v.replace(/\D/g, '') })}
                    keyboardType="numeric"
                  />
                  <Text style={styles.hoursLabel}>hours</Text>
                </View>
              </View>
            </CardRow>
            <CardRow last>
              <View style={styles.behavRow}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.behavTitle}>Tag-based Reply-To routing</Text>
                  <Text style={styles.behavSub}>Use the ticket's tag (e.g. billing) to set a custom reply-to like support+billing@acme.com.</Text>
                </View>
                <Toggle on={tagRouting} onToggle={() => updateChannel?.({ tagRouting: !tagRouting })} />
              </View>
            </CardRow>
          </Card>

          {/* Footer */}
          <View style={styles.footer}>
            <PrimaryButton title="Discard" size="md" variant="default" onPress={() => toast('Changes discarded.', 'info')} />
            <PrimaryButton
              title="Save changes"
              size="md"
              variant="primary"
              leftIcon={<Icon name="Check" size={14} color={Colors.paper} />}
              onPress={() => saveChannel?.()}
            />
          </View>
        </ScrollView>
      </View>
    </ScreenShell>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: Colors.paper },
  pageHead: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 16,
    paddingHorizontal: 28, paddingTop: 20, paddingBottom: 18,
    borderBottomWidth: 1, borderBottomColor: Colors.line,
    backgroundColor: Colors.card, flexShrink: 0,
  },
  pageTitle: { fontSize: 20, fontWeight: FontWeight.semibold, color: Colors.ink, letterSpacing: -0.2 },
  pageSub: { fontSize: 13, color: Colors.ink3, marginTop: 4 },
  enabledBadgeRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  enabledBadge: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingHorizontal: 10, paddingVertical: 4,
    borderRadius: Radii.full, borderWidth: 1,
  },
  enabledBadgeOn: { backgroundColor: Colors.successSoft, borderColor: Colors.successLine },
  enabledBadgeOff: { backgroundColor: Colors.paper3, borderColor: Colors.line },
  enabledDot: { width: 6, height: 6, borderRadius: 3 },
  enabledText: { fontSize: 12, fontWeight: FontWeight.medium },
  toggle: {
    width: 32, height: 18, borderRadius: 999, borderWidth: 1,
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}),
  },
  toggleThumb: {
    width: 14, height: 14, borderRadius: 99,
    backgroundColor: Colors.white,
    shadowColor: '#101828', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.18, shadowRadius: 2,
  },
  body: { flex: 1 },
  bodyContent: { maxWidth: 880, alignSelf: 'center', width: '100%' as any, padding: 24, gap: 0 },
  sectionH: { marginBottom: 12, marginTop: 8 },
  sectionTitle: { fontSize: 14, fontWeight: FontWeight.semibold, color: Colors.ink, letterSpacing: -0.1 },
  sectionSub: { fontSize: 12.5, color: Colors.ink3, marginTop: 2 },
  card: {
    backgroundColor: Colors.card,
    borderRadius: Radii.r3,
    borderWidth: 1, borderColor: Colors.line,
    marginBottom: 16, overflow: 'hidden',
  },
  cardRow: { padding: 16 },
  cardRowBorder: { borderBottomWidth: 1, borderBottomColor: Colors.line },
  grid2: { flexDirection: 'row', gap: 20 },
  gridStack: { flexDirection: 'column', gap: 14 },
  field: { flex: 1, gap: 4 },
  fieldLabel: { fontSize: 13, fontWeight: FontWeight.medium, color: Colors.ink },
  fieldHint: { fontSize: 12, color: Colors.ink3 },
  input: {
    borderWidth: 1, borderColor: Colors.line,
    borderRadius: Radii.r2, paddingHorizontal: 10, paddingVertical: 7,
    fontSize: 13.5, color: Colors.ink, backgroundColor: Colors.card,
  },
  inputWithIcon: { flexDirection: 'row', alignItems: 'center' },
  inputSuffix: { position: 'absolute' as any, right: 10 },
  alert: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 10,
    padding: 12, borderRadius: Radii.r3, borderWidth: 1,
  },
  alertSuccess: { backgroundColor: Colors.successSoft, borderColor: Colors.successLine },
  alertInfo: { backgroundColor: Colors.infoSoft, borderColor: Colors.infoLine },
  alertWarn: { backgroundColor: Colors.warnSoft, borderColor: Colors.warnLine },
  alertBody: { flex: 1 },
  alertTitle: { fontSize: 13, fontWeight: FontWeight.semibold },
  alertDesc: { fontSize: 12, color: Colors.ink2, marginTop: 2 },
  alertDescRow: { flexDirection: 'row', flexWrap: 'wrap', marginTop: 2 },
  alertLink: { textDecorationLine: 'underline', ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}) },
  dnsHeadRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 12 },
  dnsHeadLeft: { flex: 1, flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  dnsHeadTitle: { fontSize: 13.5, fontWeight: FontWeight.medium, color: Colors.ink },
  dnsHeadSub: { fontSize: 12, color: Colors.ink3, marginTop: 2 },
  dnsHeadBtns: { flexDirection: 'row', gap: 8 },
  dnsTable: {
    borderWidth: 1, borderColor: Colors.line,
    borderRadius: Radii.r3, overflow: 'hidden',
  },
  dnsRow: {
    flexDirection: 'row', alignItems: 'center',
    paddingHorizontal: 12, paddingVertical: 8,
  },
  dnsHeader: { backgroundColor: Colors.paper2 },
  dnsBorder: { borderBottomWidth: 1, borderBottomColor: Colors.line },
  dnsCell: { paddingRight: 8 },
  dnsHeaderText: { fontSize: 11.5, fontWeight: FontWeight.semibold, color: Colors.ink3 },
  monoText: { fontSize: 12, fontFamily: undefined },
  accentText: { color: Colors.accentInk },
  ink2Text: { color: Colors.ink2 },
  statusRow: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  verifiedText: { fontSize: 12, fontWeight: FontWeight.medium, color: Colors.success },
  pendingText: { fontSize: 12, color: Colors.ink4 },
  copyBtn: {
    width: 28, height: 28, justifyContent: 'center', alignItems: 'center',
    borderRadius: Radii.r1,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}),
  },
  inboundRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  inboundAddr: {
    flex: 1, fontSize: 13, color: Colors.ink,
    backgroundColor: Colors.paper2, paddingHorizontal: 12, paddingVertical: 8,
    borderWidth: 1, borderColor: Colors.line, borderRadius: Radii.r2,
  },
  inboundTip: { fontSize: 12, color: Colors.ink3, marginTop: 10 },
  behavRow: { flexDirection: 'row', alignItems: 'center', gap: 16 },
  behavTitle: { fontSize: 13.5, fontWeight: FontWeight.medium, color: Colors.ink },
  behavSub: { fontSize: 12, color: Colors.ink3, marginTop: 2 },
  csatDelayRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  csatDelayInput: { width: 64, textAlign: 'center' },
  hoursLabel: { fontSize: 12.5, color: Colors.ink3 },
  footer: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8, marginTop: 24 },
});
